import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { Divider } from '@material-ui/core';
import Search, { searchFilter } from 'components/Search';
import Paging from './Paging';
import Loader from 'components/Loader';
import { IllustrationInfo } from 'components';

const useStyles = makeStyles((theme) => ({
  containerPanel: {
    width: 'calc(100%-40px)',
    backgroundColor: theme.palette.background.paper,
    height: 'fit-content',
    borderRadius: 10,
    padding: 18,
    margin: '10px 0px',
    display: 'flex',
  },
  containerCard: {
    width: 'calc(100%-20px)',
    height: 'fit-content',
    padding: 10,
    display: 'flex',
  },
  containerActive: {
    backgroundColor: '#1ab7a7',
    '&:hover': {
      backgroundColor: '#1ab7a79e',
    },
  },
  selectArea: {
    cursor: 'pointer',
  },
  defaultArea: {
    cursor: 'default',
  },
  whiteBackground: {
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.background.hover,
    },
  },
  greyBackground: {
    backgroundColor: '#e9e9e9',
    '&:hover': {
      backgroundColor: theme.palette.background.hover,
    },
  },
}));

const List = (props) => {
  const classes = useStyles();
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);

  const {
    list = [],
    children,
    type = 'panel', // panel, card
    pagination = 5,
    search = {},
    withAnimation,
    isReady = true,
    panelActive = null,
    idActive = 'id',
    withoutSelectArea = false,
    padding,
    cardMarginBottom,
    pagingPosition,
    pagingType,
    pagingFirstLast = false,
    paginationProps = {},
    withoutSearch = false,
    inActiveColor = 'white', // white, grey,
    renderHeader,
    renderRightSearch,
    ...rest
  } = props;
  const { placeholder = 'Search', width, ...searchProps } = search;

  const data = searchFilter(keyword)(list);

  if (keyword) {
    data.sort((a, b) => {
      if (a.fullname?.toLowerCase().includes(keyword.toLowerCase())) {
        return -1;
      } else if (b.fullname?.toLowerCase().includes(keyword.toLowerCase())) {
        return 1;
      }
      return 0;
    });
  }

  const maxPage = data.length === 0 ? 0 : Math.ceil(data.length / pagination) - 1;

  const startNumber = pagination * page;
  const endNumber = startNumber + pagination;

  if (isReady) {
    return (
      <>
        {!withoutSearch && (
          <div style={{ display: 'flex' }}>
            <Search
              placeholder={placeholder + ' . . .'}
              onChange={(keyword) => {
                setPage(0);
                setKeyword(keyword);
              }}
              width={width || 200}
              fullWidth={type === 'card' ? !width : false}
              variant={type === 'card' ? 'filled' : 'textField'}
              {...searchProps}
            />
            {renderRightSearch && renderRightSearch()}
          </div>
        )}
        {renderHeader && renderHeader()}
        {data.length > 0 ? (
          <>
            {data
              .slice(startNumber, endNumber > data.length ? data.length : endNumber)
              .map((item, key) => (
                <Fragment key={key}>
                  <div
                    className={clsx({
                      zoomIn: typeof withAnimation === 'boolean' ? withAnimation : type === 'panel',
                      [classes.containerPanel]: type === 'panel',
                      [classes.containerCard]: type === 'card',
                      // [classes.containerActive]: (panelActive === item[idActive]),
                      [classes.selectArea]: !withoutSelectArea,
                      [classes.defaultArea]: withoutSelectArea,
                      [classes.whiteBackground]:
                        !withoutSelectArea &&
                        panelActive !== item[idActive] &&
                        inActiveColor === 'white',
                      [classes.greyBackground]:
                        !withoutSelectArea &&
                        panelActive !== item[idActive] &&
                        inActiveColor === 'grey',
                    })}
                    style={{
                      padding: padding && padding,
                    }}
                    {...rest}
                  >
                    {children(item, item[idActive], panelActive === item[idActive], key)}
                  </div>
                  {type === 'card' && (
                    <Divider width="100%" style={{ marginBottom: cardMarginBottom || 25 }} />
                  )}
                </Fragment>
              ))}
            <Paging
              page={page}
              setPage={setPage}
              maxPage={maxPage}
              position={pagingPosition || (type === 'card' ? 'right' : 'left')}
              type={pagingType || (type === 'card' ? 'simple' : 'full')}
              withoutFirstLast={!pagingFirstLast}
              {...paginationProps}
            />
          </>
        ) : (
          <IllustrationInfo width={type === 'card' ? 100 : 300} />
        )}
      </>
    );
  } else {
    return <Loader height={450} variant="clip" />;
  }
};

List.propTypes = {
  list: PropTypes.array,
  type: PropTypes.oneOf(['panel', 'card']),
  pagination: PropTypes.number,
  search: PropTypes.object,
  withAnimation: PropTypes.bool,
  inActiveColor: PropTypes.oneOf(['white', 'grey']),
};

export default List;
